import axiosClient from './axiosClient';

/**
 * Cross-entity helpers that cut across the globals/* surfaces (tasks,
 * adventures, catalogs) and talk to the dependency-scan + tenant-impact
 * endpoints the R4 backend exposes.
 *
 * These are deliberately entity-agnostic: the caller passes the entity
 * kind as a slug ('task', 'adventure', 'task_type', 'task_reward_type',
 * 'wallet_type', …) so the Dependency / Impact dialogs can be driven
 * from a single thin layer instead of duplicating per-entity wrappers.
 *
 * Server-side contract lives in `server/src/routes/admin/globals/deps.ts`:
 *   GET /api/admin/globals/:entity/:code/dependencies
 *   GET /api/admin/globals/:entity/:code/affected-tenants
 */

export type GlobalEntity =
  | 'task'
  | 'adventure'
  | 'cohort'
  | 'sweepstakes'
  | 'terms_of_service'
  | 'questionnaire'
  | 'task_reward_collection'
  | 'plan_cohort_purse_mapping'
  | 'task_type'
  | 'task_category'
  | 'task_reward_type'
  | 'wallet_type'
  | 'component_type'
  | 'onboarding_component'
  | 'notification_channel'
  | 'notification_category'
  | 'canonical_field'
  | 'cohort_attribute_def';

/** Preview row for a single dependent (tenant-scoped reference). */
export interface DependencyPreviewRow {
  /** Name shown in the dialog list ("Acme Corp", etc). */
  name: string;
  /** Tenant code — usable as a deep-link segment. */
  tenant_code: string;
  /** Short kind-of-dependent description ("action_reward", "component"…). */
  kind?: string;
  /** How many references this dependent holds (defaults to 1 when absent). */
  reference_count?: number;
}

export interface DependencyScanResponse {
  /** Total dependent rows across all tenants. */
  count: number;
  /**
   * First N rows (server-capped; we display 5 and mention the rest).
   * Always safe to iterate — missing server field degrades to empty.
   */
  preview: DependencyPreviewRow[];
  /** Optional roll-up by tenant (count per tenant_code). */
  by_tenant?: Array<{ tenant_code: string; count: number }>;
}

export interface AffectedTenantsResponse {
  count: number;
  preview: Array<{
    tenant_code: string;
    name?: string;
    /**
     * When populated, indicates the tenant's current in-use platform
     * version so the R5 drawer can surface "stale" tenants.
     */
    platform_version?: number | null;
  }>;
}

const depsBase = (entity: GlobalEntity, code: string): string =>
  `/api/admin/globals/${entity}/${encodeURIComponent(code)}`;

export const adminGlobalsApi = {
  /**
   * Dependency scan — used by DependencyImpactDialog Variants B/C to
   * decide whether a deactivate is safe (A), warning-worthy (B) or
   * outright blocked (C).
   *
   * The dialog calls this on `open === true`; a missing / 404 response
   * is treated as "no dependents" so older servers keep working.
   */
  getDependencies: async (
    entity: GlobalEntity,
    code: string,
  ): Promise<DependencyScanResponse> => {
    const res = await axiosClient.get<DependencyScanResponse>(
      `${depsBase(entity, code)}/dependencies`,
    );
    return res.data;
  },

  /**
   * Tenant-impact count — used by the PublishConfirmationDialog's
   * "affecting M tenants" badge and the impending R5 drawer. Returns
   * at most `preview` rows; caller should show "+N more" for overflow.
   */
  getAffectedTenants: async (
    entity: GlobalEntity,
    code: string,
    preview: number = 5,
  ): Promise<AffectedTenantsResponse> => {
    const res = await axiosClient.get<AffectedTenantsResponse>(
      `${depsBase(entity, code)}/affected-tenants`,
      { params: { preview } },
    );
    return res.data;
  },
};
